
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, BarChart, Clock, ArrowRight } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface QuizResult {
  id: string;
  session_id: string;
  score: number;
  total_questions: number;
  percentage: number;
  difficulty: string;
  date_filter: string | null;
  time_period: string | null;
  completed_at: string;
}

const CurrentAffairsHistory = () => {
  const { userId } = useAuthSession();
  const [history, setHistory] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('current_affairs_quiz_results')
          .select('*')
          .eq('user_id', userId)
          .order('completed_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching quiz history:', error);
          return;
        }
        
        setHistory(data || []);
      } catch (err) {
        console.error('Failed to fetch quiz history:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
  }, [userId]);

  const viewDetails = (resultId: string) => {
    navigate(`/current-affairs/quiz-results/${resultId}`);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle><Skeleton className="h-6 w-48" /></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border rounded-md p-4 space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-48" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Quiz History</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-12">
          <p className="text-muted-foreground mb-4">No quiz history found</p>
          <Button 
            onClick={() => navigate('/current-affairs/quiz')}
            variant="outline"
          >
            Try a Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Current Affairs Quiz History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {history.map((result) => (
            <div key={result.id} className="border rounded-md p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold">
                    Current Affairs Quiz {result.time_period && `(${result.time_period})`}
                  </h3>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    {format(new Date(result.completed_at), 'MMM d, yyyy • h:mm a')}
                  </div>
                </div>
                <Badge className={`${result.difficulty === 'hard' ? 'bg-red-500' : result.difficulty === 'medium' ? 'bg-blue-500' : 'bg-green-500'} text-white`}>
                  {result.difficulty.charAt(0).toUpperCase() + result.difficulty.slice(1)}
                </Badge>
              </div>
              
              <Separator className="my-2" />
              
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="flex items-center">
                  <BarChart className="h-4 w-4 mr-1.5 text-muted-foreground" />
                  <span className="text-sm">{result.score}/{result.total_questions} correct</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1.5 text-muted-foreground" />
                  <span className="text-sm">{result.percentage}% score</span>
                </div>
              </div>
              
              <Button 
                variant="ghost" 
                className="w-full mt-2 text-xs"
                onClick={() => viewDetails(result.id)}
              >
                View Details
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrentAffairsHistory;
